import React, { useMemo, useState } from 'react'
import { useTranslation } from 'language/translator'
import { cloneDeep } from 'lodash'

import {
  InnerWidgetPatchBody,
  KeyFigureVisualizationType,
  PieChartValueVisualizationType,
  TWidgetSettings,
  WidgetType
} from 'types/GlobalWidget'

import {
  translateBreakdownByDisplayType,
  translatePieChartValueVisualizationType,
  translateWidgetType
} from 'utils/enumTranslator'

import Box from 'components_new/atoms/Box'
import Grid from 'components_new/atoms/Grid'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Switch from 'components_new/atoms/Switch'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SettingsToggleOption from 'components_new/molecules/SettingsToggleOption'
import ToggleButton from 'components_new/molecules/ToggleButton'

import {
  DEFAULT_TYPES,
  ORDER,
  breakdownByDisplayTypeOptions,
  hasBreakdown
} from './utils'

interface VisualizationSectionProps {
  settings: TWidgetSettings
  updateWidget: (body: InnerWidgetPatchBody) => void
}

const LEGAL_TYPES = [
  WidgetType.BAR_CHART,
  WidgetType.BUBBLE_CHART,
  WidgetType.COMBO_CHART,
  WidgetType.FUNNEL,
  WidgetType.KEY_FIGURE,
  WidgetType.LINE_CHART,
  WidgetType.LIST,
  WidgetType.MAP,
  WidgetType.PIE_CHART
]

const VisualizationSection = (props: VisualizationSectionProps) => {
  const { settings, updateWidget } = props
  const { t, translateEnum } = useTranslation()

  const [showMore, setShowMore] = useState<boolean>(
    !DEFAULT_TYPES.includes(settings.type.selected)
  )

  const visualizationOptions = useMemo(() => {
    const options = cloneDeep(settings.type.options)

    if (showMore) {
      return options.sort(
        (a, b) => ORDER.indexOf(a.value) - ORDER.indexOf(b.value)
      )
    } else {
      return options
        .filter((option) => DEFAULT_TYPES.includes(option.value))
        .sort((a, b) => ORDER.indexOf(a.value) - ORDER.indexOf(b.value))
    }
  }, [showMore, settings.type.options])

  if (!LEGAL_TYPES.includes(settings.type.selected)) {
    return null
  }

  /*-- toggle --*/
  const handleToggle = (
    property:
      | 'horizontal'
      | 'show_header'
      | 'show_total'
      | 'show_vertical_dividers'
  ) => {
    let body: InnerWidgetPatchBody = {}

    switch (property) {
    case 'horizontal':
      body = {
        horizontal: !settings.horizontal
      }
      break
    case 'show_header':
      body = {
        show_header: !settings.show_header
      }
      break
    case 'show_total':
      body = {
        show_total: !settings.show_total
      }
      break
    case 'show_vertical_dividers':
      body = {
        show_vertical_dividers: !settings.show_vertical_dividers
      }
      break
    }

    updateWidget(body)
  }

  return (
    <SettingsGroup title={t('widgets', 'visualization_title')}>
      <SettingsItem>
        <Grid container={true} columns={5}>
          {visualizationOptions.map(({ value, disabled }) => {
            const selected = value === settings.type.selected
            const translatedWidgetType = translateEnum('widget_types', value)

            return (
              <Grid item={true} key={value} xs={1}>
                <SettingsToggleOption
                  disabled={disabled}
                  badge={value === WidgetType.MAP}
                  iconName={translateWidgetType[value].iconName}
                  onClick={() => updateWidget({ type: value })}
                  selected={selected}
                  title={translatedWidgetType.title}
                  tooltip={
                    disabled ? (
                      <>
                        {disabled ? translatedWidgetType.disabledTitle : null}
                      </>
                    ) : undefined
                  }
                />
              </Grid>
            )
          })}
        </Grid>
      </SettingsItem>
      <SettingsItem
        color="info"
        onClick={() => setShowMore(!showMore)}
        title={showMore ? t('global', 'show_less') : t('global', 'show_more')}
        variant="action"
      />

      {/*-- horizontal/vertical --*/}
      {[WidgetType.BAR_CHART].includes(settings.type.selected) ? (
        <SettingsItem
          actions={
            <Switch
              checked={settings.horizontal}
              onChange={() => handleToggle('horizontal')}
              size="small"
            />
          }
          title={t('widgets', 'show_horizontal')}
        />
      ) : null}

      {/*-- breakdown --*/}
      {[WidgetType.BAR_CHART, WidgetType.COMBO_CHART].includes(
        settings.type.selected
      ) && hasBreakdown({ settings }) ? (
            <SettingsItem
              actions={
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {breakdownByDisplayTypeOptions.map((option) => {
                    // eslint-disable-next-line max-len
                    const selected = settings.breakdown_by_display_type === option

                    return (
                      <Tooltip
                        key={option}
                        title={
                          translateEnum('breakdown_by_display_types', option).title
                        }
                      >
                        <IconButton
                          color={selected ? 'primary' : undefined}
                          onClick={() =>
                            updateWidget({
                              breakdown_by_display_type: option
                            })
                          }
                          size="small"
                          sx={{
                            borderRadius: 1,
                            border: selected ? '1px solid black' : undefined
                          }}
                        >
                          <Icon
                            fontSize="small"
                            // eslint-disable-next-line max-len
                            name={translateBreakdownByDisplayType[option].iconName}
                          />
                        </IconButton>
                      </Tooltip>
                    )
                  })}
                </Box>
              }
              title={t('widgets', 'breakdown_title')}
            />
          ) : null}

      {/*-- pie chart --*/}
      {[WidgetType.PIE_CHART].includes(settings.type.selected) ? (
        <SettingsItem
          actions={
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.values(PieChartValueVisualizationType).map((option) => {
                const selected =
                  settings.pie_chart_value_visualization_type === option

                return (
                  <Tooltip
                    key={option}
                    title={translateEnum(
                      'pie_chart_value_visualization_types',
                      option
                    )}
                  >
                    <IconButton
                      color={selected ? 'primary' : undefined}
                      onClick={() =>
                        updateWidget({
                          pie_chart_value_visualization_type: option
                        })
                      }
                      size="small"
                      sx={{
                        borderRadius: 1,
                        border: selected ? '1px solid black' : undefined
                      }}
                    >
                      <Icon
                        fontSize="small"
                        name={
                          translatePieChartValueVisualizationType[option]
                            .iconName
                        }
                      />
                    </IconButton>
                  </Tooltip>
                )
              })}
            </Box>
          }
          title={t('widgets', 'pie_segments')}
        />
      ) : null}

      {/*-- show header/lines/total --*/}
      {[WidgetType.LIST].includes(settings.type.selected) ? (
        <>
          <SettingsItem
            actions={
              <Switch
                checked={settings.show_header}
                onChange={() => handleToggle('show_header')}
                size="small"
              />
            }
            title={t('widgets', 'show_headers')}
          />
          <SettingsItem
            actions={
              <Switch
                checked={settings.show_total}
                onChange={() => handleToggle('show_total')}
                size="small"
              />
            }
            disabled={
              settings.is_preview || settings.variant_options.length > 0
            }
            disabledText={
              settings.variant_options.length > 0
                ? t('widgets', 'disabled_show_total_variant_options')
                : t('widgets', 'disabled_show_total_if_preview')
            }
            title={t('widgets', 'show_total')}
          />
          <SettingsItem
            actions={
              <Switch
                checked={settings.show_vertical_dividers}
                onChange={() => handleToggle('show_vertical_dividers')}
                size="small"
              />
            }
            title={t('widgets', 'show_vertical_lines')}
          />
          <SettingsItem
            actions={
              <Switch
                checked={settings.horizontal}
                onChange={() => handleToggle('horizontal')}
                size="small"
              />
            }
            disabled={settings.kpi_options.some(
              (kpiOption) => !!kpiOption.breakdown_by?.selected
            )}
            disabledText={t(
              'widgets',
              'disabled_segments_horizontal_if_breakdown'
            )}
            title={t('widgets', 'show_segments_horizontal')}
          />
        </>
      ) : null}

      {/*-- variant of key figure --*/}
      {[WidgetType.KEY_FIGURE].includes(settings.type.selected) ? (
        <SettingsItem
          actions={
            <ToggleButton
              items={Object.values(KeyFigureVisualizationType).map((type) => {
                return {
                  title: (
                    <Text variant={'body2'}>
                      {translateEnum('key_figure_visualization_types', type)}
                    </Text>
                  ),
                  value: type
                }
              })}
              value={settings.key_figure_visualization_type}
              fullWidth={false}
              size={'small'}
              onChange={(_, value) => {
                if (value) {
                  updateWidget({
                    key_figure_visualization_type:
                      value as KeyFigureVisualizationType
                  })
                }
              }}
            />
          }
          title={t('global', 'variant')}
        />
      ) : null}
    </SettingsGroup>
  )
}

export default VisualizationSection
